/**
 * Sección compacta de BPIN dentro del header del expediente.
 *
 * - Sin BPIN: muestra prompt informativo + input + botón "Guardar".
 *   Se enfatiza visualmente cuando el backend marca el expediente como
 *   radicado sin BPIN (`bpin_pending`, la regla del banner global, issue
 *   #823): el funcionario ya radicó pero olvidó registrarlo.
 * - Con BPIN: muestra el código en pill + botón "Editar" para
 *   corregir typos.
 *
 * Validación frontend: 14 dígitos. Si falla, muestra error inline.
 * Backend valida con regex `^\d{14}$` y rechaza con 422.
 */

import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { track } from "@/lib/analytics";
import { updateProject, type ProjectDetailDTO } from "@/lib/api";
import { validateBpin } from "@/lib/bpin";
import { useActiveMunicipalityDane } from "@/lib/municipality";
import { BPIN_ANCHOR_ID } from "./focus";

export function BpinSection({
  project,
  onChanged,
}: {
  project: ProjectDetailDTO;
  onChanged: () => void;
}) {
  const activeDane = useActiveMunicipalityDane();
  const [editing, setEditing] = useState(false);
  const [bpinInput, setBpinInput] = useState(project.bpin ?? "");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (value: string) => updateProject(project.id, activeDane, { bpin: value }),
    onSuccess: () => {
      track({
        event_name: "project_bpin_registered",
        properties: { project_id: project.id },
      });
      setEditing(false);
      setError(null);
      onChanged();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Error al guardar el BPIN.");
    },
  });

  const cleanBpin = bpinInput.replace(/\D/g, "");
  // Validación semántica completa (formato + año + DANE) — espejo del
  // backend para feedback inline. Sprint 11 follow-up #5.
  const validation = validateBpin(cleanBpin, activeDane);
  const isValid = validation.valid;
  // Resaltamos que falta el BPIN con la misma regla del banner global, que
  // calcula el backend. La lista propia que habia aca contaba `closed` como
  // radicado y el banner no (issue #823).
  const missingPostSubmit = project.bpin_pending;

  // Caso: ya hay BPIN guardado y no estamos editando.
  if (project.bpin && !editing) {
    return (
      <div
        data-testid="bpin-display"
        className="mt-3 inline-flex items-center gap-2 rounded-md bg-slate-50 px-3 py-1.5"
      >
        <span className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
          BPIN
        </span>
        <code className="font-mono text-sm font-semibold text-slate-900">{project.bpin}</code>
        <button
          type="button"
          onClick={() => {
            setBpinInput(project.bpin ?? "");
            setEditing(true);
          }}
          data-testid="bpin-edit"
          className="text-xs font-medium text-brand-700 hover:underline"
        >
          editar
        </button>
      </div>
    );
  }

  // Caso: sin BPIN o editando — mostrar input.
  return (
    <div
      id={BPIN_ANCHOR_ID}
      style={{ scrollMarginTop: "5rem" }}
      data-testid="bpin-section"
      data-tour-id="bpin-section"
      className={
        "mt-3 rounded-md border p-3 " +
        (missingPostSubmit ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50")
      }
    >
      <p className="mb-1 text-xs font-semibold text-slate-700">Código BPIN del proyecto</p>
      <p className="mb-2 text-xs text-slate-600">
        {/* "Cuando radiques" solo en borrador: un cerrado o rechazado no esta
            por radicarse (issue #881). */}
        {missingPostSubmit
          ? "Tu expediente ya está radicado pero todavía no tiene BPIN registrado."
          : project.status === "draft"
            ? "Cuando radiques el proyecto en PIIP, registra acá el BPIN (14 dígitos: año + DANE + consecutivo)."
            : "Registra acá el BPIN del proyecto en PIIP (14 dígitos: año + DANE + consecutivo)."}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder="2026073347001234"
          value={bpinInput}
          onChange={(e) => setBpinInput(e.target.value)}
          data-testid="bpin-input"
          aria-label="Código BPIN"
          className="w-56 rounded-md border border-slate-300 px-2 py-1 font-mono text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-hidden"
        />
        <button
          type="button"
          disabled={!isValid || mutation.isPending}
          onClick={() => mutation.mutate(cleanBpin)}
          data-testid="bpin-save"
          className="rounded-md bg-brand-700 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
        >
          {mutation.isPending ? "Guardando…" : "Guardar"}
        </button>
        {editing && (
          <button
            type="button"
            onClick={() => {
              setEditing(false);
              setBpinInput(project.bpin ?? "");
              setError(null);
            }}
            className="text-xs font-medium text-slate-500 hover:text-slate-700"
          >
            Cancelar
          </button>
        )}
        <span className="text-[11px] text-slate-500">{cleanBpin.length}/14 dígitos</span>
      </div>
      {validation.reason && (
        <p data-testid="bpin-validation-error" className="mt-1 text-xs text-red-600">
          {validation.reason}
        </p>
      )}
      {error && !validation.reason && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
